import { uniqBy } from "lodash-es";
import { type VFC } from "react";
import { useSelector } from "react-redux";
import { useMatch, useParams } from "react-router";

import { toSafeInt } from "widgets/utils";

import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericEntityGenomeView from "../genome-viewer/GenericEntityGenomeView.js";
import { type IGenomeViewOwnProps } from "../genome-viewer/GenomeView.js";
import { renderVariantAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { getPaddedRange } from "@/app-utils/range.ts";
import { type Organism, type Variant } from "@/graphql.ts";
import { getClassicGenomeBrowserTracks } from "@/redux/modules/genome-browser.ts";
import { type IMutationState, shouldLoadGenMutation } from "@/redux/modules/mutation.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { TRACK_TYPE, UNKNOWN_GENOME_ID } from "@/settings/settings.js";

const VariantGenomeView: VFC = () => {
  const { mutation } = useSelector<IAppState, IMutationState>((state) => state.mutation);
  const { variantId } = useParams<"variantId">();
  const match = useMatch("/variant/:variantId/genome/:genomeId");
  const genomeId = match?.params.genomeId;
  const load = useLoadDispatchWithIdAsNumber(shouldLoadGenMutation, variantId);

  // only the variants summary has a genome selector, and since the genomeId is not always provided as part of the URL
  // we need to sustain it across changes (e.g. ref genome changed by user leads to change the mutation data, leads to pick the default genome)
  let selectedVariant: Variant;

  const getVariantToDisplay = (relatedVariants: Variant[]): Variant => {
    // we assume the mutation is present in the pre-selected mutant genome (part of the URL), otherwise
    // the variant used for the display information depends on the genome selection ; if no selection has been made (id is part of the URL) then we choose the default
    if (genomeId) {
      // we assume the mutation is present in the pre-selected mutant genome (part of the URL)
      // otherwise it means that the chosen ref genome is not compatible with the chosen genome [SEL6-2348]
      selectedVariant = relatedVariants.find((v: Variant) => Number.parseInt(v.genome.id) === toSafeInt(genomeId));
    } else {
      // if the genome id is not part of the URL then usually we choose the ref variant (and related genome)
      // except if we stay in context of the view (browsing through a list or change of the ref genome leading to different perspective)
      // in which case we try to sustain the previously selected genome
      selectedVariant =
        selectedVariant &&
        relatedVariants.find(
          (v: Variant) => Number.parseInt(v.genome.id) === Number.parseInt(selectedVariant.genome.id),
        );
      if (!selectedVariant) {
        selectedVariant = mutation.refVariant;
      }
    }
    return selectedVariant;
  };

  let genomeViewProps: IGenomeViewOwnProps;

  if (Number.parseInt(mutation?.id) === toSafeInt(variantId)) {
    // all the genomes that can be selected include the reference genomes and the mutant genomes ;
    // we should not make use of the variantAnalysisGroup here because the mutation in context might not exist for some genomes of the analysis group
    // and we would not know which region to display
    const relatedVariants: Variant[] = (mutation.refVariant?.refVariants || [])
      .concat(mutation.refVariant || [])
      .concat(mutation.mutVariants || []);

    const variant: Variant = getVariantToDisplay(relatedVariants);
    // the list of available genomes to pick (for changing the perspective) is derived from the list of available variants
    const availableGenomes: Organism[] = uniqBy(
      relatedVariants.map((v: Variant) => v.genome),
      "id",
    );
    // [SEL6-2348] it might happen that the variant does not exist in the selected genome in respect to the chosen reference, in this case we ask the user to select another genome
    const selectionTooltipText: string =
      !variant &&
      `Select a genome that contains mutation ${mutation.refVariant?.name} from reference genome ${mutation.refVariant?.refGenome.name} perspective`;

    genomeViewProps = {
      contigId: variant && variant.region.contig.id,
      range: variant && getPaddedRange(variant.region.range, variant.region.contig.length),
      genomeId: (variant && variant.genome.id) || UNKNOWN_GENOME_ID,
      selectionTooltipText: selectionTooltipText,
      genomes: availableGenomes,
      defaultTracks: getClassicGenomeBrowserTracks([TRACK_TYPE.SEQUENCE, TRACK_TYPE.GENE, TRACK_TYPE.VARIANT]),
      attributesWidgets: [
        <AttributesListWidget
          attributes={renderVariantAttributesWidgets(mutation?.refVariant)}
          key={"variant-genome-view-widget-1"}
        />,
      ],
      title: mutation?.refVariant?.name,
      subtitle: mutation?.refVariant?.description || "Genome View",
    };
  }

  return <GenericEntityGenomeView genomeViewProps={genomeViewProps} loadEntityData={() => void load()} />;
};

export default VariantGenomeView;
